import ReactDOM from 'react-dom';
import { Editor } from 'slate';
import {
  deleteBlock,
  getBlocksFieldname,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers/Blocks/Blocks';
import { getPreviousVoltoBlock } from '@plone/volto-slate/utils/volto-blocks';
import { isCursorAtBlockStart } from '@plone/volto-slate/utils/selection';

type KeyboardHandlerArgs = { editor: any; event: KeyboardEvent };
type KeyboardHandler = (args: KeyboardHandlerArgs) => boolean | undefined;

/**
 * Guarda o handler de Backspace do volto-slate contra blocos que não são de
 * texto.
 *
 * `joinWithPreviousBlock` junta o bloco de texto atual ao anterior quando o
 * cursor está no começo dele, mas nunca confere o tipo do anterior: entra
 * direto em `mergeSlateWithBlockBackward`, que faz `[...prevBlock.value]`, ou
 * em `getBlockEndAsRange`, que faz `value.length`. Se o bloco anterior não for
 * `slate` — o `title`, uma imagem, uma tabela, qualquer bloco nosso — não há
 * `value` e o formulário inteiro cai pelo ErrorBoundary:
 * "can't access property Symbol.iterator, prevBlock.value is undefined".
 *
 * O `title` é o bloco 0 de toda página, então bastava apagar para trás no
 * primeiro parágrafo do conteúdo.
 *
 * A guarda é a mesma que o handler simétrico do core já faz no Delete
 * (`joinWithNextBlock` desiste quando o próximo bloco não é `slate`). Aqui,
 * quando o anterior não é texto:
 *
 * - com o parágrafo vazio, ele é apagado e o bloco anterior fica selecionado,
 *   que é o que o handler original faria no lugar do merge;
 * - com o parágrafo escrito, não se faz nada — não há como juntar texto a um
 *   bloco que não tem texto.
 */
export const withPreviousBlockGuard = (
  joinWithPreviousBlock: KeyboardHandler,
): KeyboardHandler =>
  function joinWithPreviousTextBlock({ editor, event }) {
    if (!isCursorAtBlockStart(editor)) return;

    const { block, index, properties, data, onChangeField, onSelectBlock } =
      editor.getBlockProps();

    const prev = getPreviousVoltoBlock(index, properties);
    if (!prev) return;
    const [prevBlock = {}, prevBlockId] = prev;

    if (prevBlock['@type'] === 'slate')
      return joinWithPreviousBlock({ editor, event });

    if (data?.required || prevBlock?.required) return;
    if (Editor.string(editor, [])) return;

    event.preventDefault();
    event.stopPropagation();

    const blocksFieldname = getBlocksFieldname(properties);
    const blocksLayoutFieldname = getBlocksLayoutFieldname(properties);
    // O `intl` do core só seria usado se a página ficasse sem bloco nenhum,
    // e os handlers de teclado do volto-slate também o chamam sem ele.
    const newFormData = deleteBlock(properties, block, undefined);

    ReactDOM.unstable_batchedUpdates(() => {
      onChangeField(blocksFieldname, newFormData[blocksFieldname]);
      onChangeField(blocksLayoutFieldname, newFormData[blocksLayoutFieldname]);
      onSelectBlock(prevBlockId);
    });

    return true;
  };

export default withPreviousBlockGuard;
